import React, { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Snackbar, Alert,
} from '@mui/material';
import type { StaffingRuleView } from '../../../../services/LaborManagementService/types/laborManagementTypes';
import { useCommittedNumberField } from './useCommittedNumberField';

// A staleness refusal arrives as an RTK Query rejection carrying a 409 and the conflict body, and
// the page answers it with its own warning. Read here rather than imported from
// `useScopedRuleSave`, because that module pulls in the service layer and this component's test
// renders without one. Both halves are needed, and this is why it is not the shorter status-only
// line `RulesEditorGrid.tsx` carries: `asConflict` there raises the warning only when the body is
// present, so a bodyless 409 must still read as a plain failure or the field reverts with nothing
// on screen at all.
const isStaleSaveRefusal = (error: unknown) => {
  const rejection = error as { status?: unknown; data?: unknown } | undefined;
  return rejection?.status === 409 && !!rejection.data;
};

const DEFAULT_DAY_LENGTH_HOURS = 8;
const DEFAULT_TRAYS_PER_ROOM = 1;

/** A shift length has to be a positive number of hours that fits inside a day. */
const isValidDayLength = (hours: number) => Number.isFinite(hours) && hours > 0 && hours <= 24;

/** Trays per room is a whole count, and at least one. */
const isValidTraysPerRoom = (trays: number) => Number.isFinite(trays) && trays >= 1;

const parseDayLength = (draft: string) => {
  const hours = parseFloat(draft);
  return isValidDayLength(hours) ? hours : null;
};

const parseTraysPerRoom = (draft: string) => {
  const trays = parseInt(draft, 10);
  return isValidTraysPerRoom(trays) ? trays : null;
};

interface FacilityConfigurationPanelProps {
  staffingRule: StaffingRuleView;
  isSaving: boolean;
  isAdmin: boolean;
  /**
   * Sends only the setting that was committed. The department rules never leave the browser.
   * A rejection puts the stored value back in the field.
   */
  onSaveSettings: (settings: {
    defaultOperatingDayLengthHours?: number;
    defaultTraysPerRoom?: number;
  }) => Promise<unknown> | void;
}

/** Facility-wide settings. Both fields commit on blur or Enter — see useCommittedNumberField. */
const FacilityConfigurationPanel: React.FC<FacilityConfigurationPanelProps> = ({
  staffingRule, isSaving, isAdmin, onSaveSettings,
}) => {
  const [showFailure, setShowFailure] = useState(false);

  // A staleness refusal already has its own warning on the page, and a refusal must not read as a
  // generic failure. Every other rejection does, and the field still reverts because this rethrows.
  const saveSettings: FacilityConfigurationPanelProps['onSaveSettings'] = async (settings) => {
    try {
      await onSaveSettings(settings);
    } catch (err) {
      if (!isStaleSaveRefusal(err)) setShowFailure(true);
      throw err;
    }
  };

  const dayLengthField = useCommittedNumberField({
    value: staffingRule.defaultOperatingDayLengthHours || DEFAULT_DAY_LENGTH_HOURS,
    parse: parseDayLength,
    onCommit: (hours) => saveSettings({ defaultOperatingDayLengthHours: hours }),
  });

  const traysField = useCommittedNumberField({
    value: staffingRule.defaultTraysPerRoom || DEFAULT_TRAYS_PER_ROOM,
    parse: parseTraysPerRoom,
    onCommit: (trays) => saveSettings({ defaultTraysPerRoom: trays }),
  });

  const disabled = isSaving || !isAdmin;

  return (
    <Paper
      elevation={0}
      sx={{ mb: 3, p: 2, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', mb: 1.5 }}>
        Facility Configuration
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">Operating Day Length (hrs)</Typography>
        <TextField
          type="number"
          size="small"
          {...dayLengthField}
          disabled={disabled}
          inputProps={{
            min: 1, max: 24, step: 0.5, 'aria-label': 'Operating Day Length (hrs)',
          }}
          sx={{ width: 100 }}
        />
        <Typography variant="caption" color="text.secondary">
          Standard shift length for this facility (typically 8 or 10)
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
        <Typography variant="body2" color="text.secondary">Default Trays per Room</Typography>
        <TextField
          type="number"
          size="small"
          {...traysField}
          disabled={disabled}
          inputProps={{ min: 1, step: 1, 'aria-label': 'Default Trays per Room' }}
          sx={{ width: 100 }}
        />
        <Typography variant="caption" color="text.secondary">
          Trays derived per room when no daily tray count is entered
        </Typography>
      </Box>
      <Snackbar
        open={showFailure}
        autoHideDuration={4000}
        onClose={() => setShowFailure(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setShowFailure(false)} sx={{ width: '100%' }}>
          Failed to save facility settings.
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default FacilityConfigurationPanel;
